// ProfileTabs.tsx — Controlled tab bar for the profile page.
//
// Renders a `Tabs` component with a tab list that depends on:
//   - `isOwn`: own profile adds "Cherished" + "Bookmarks"
//   - `user.likesPublic`: other-user profile adds "Cherished" only when public (the
//     field keeps its old name; Likes were merged into Cherish)
//   - "Videos" (1.27.60): the member's channel — every profile has it, and it is the one
//     tab a private account's non-follower can still fill when the channel is public

import { Tabs } from '@/ui';
import type { TabItem } from '@/ui';
import type { User } from '@/core/models';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ProfileTabsProps {
  user: User;
  isOwn: boolean;
  active: string;
  onChange: (v: string) => void;
}

// ─── Tab definitions ─────────────────────────────────────────────────────────

const BASE_TABS: TabItem[] = [
  { value: 'posts',   label: 'Posts'   },
  { value: 'replies', label: 'Replies' },
  { value: 'media',   label: 'Media'   },
  { value: 'videos',  label: 'Videos'  },
];

function buildTabs(user: User, isOwn: boolean): TabItem[] {
  if (isOwn) {
    return [
      ...BASE_TABS,
      { value: 'likes',     label: 'Cherished' },
      { value: 'bookmarks', label: 'Bookmarks' },
    ];
  }
  if (user.likesPublic === true) {
    return [...BASE_TABS, { value: 'likes', label: 'Cherished' }];
  }
  return BASE_TABS;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ProfileTabs({ user, isOwn, active, onChange }: ProfileTabsProps) {
  const items = buildTabs(user, isOwn);
  return <Tabs items={items} value={active} onChange={onChange} />;
}
